import React, { useState, useEffect } from "react";
import { getData } from "./firestoreService";
import { patchPatientDirectory } from "./patientDirectoryService";
import { useAuth } from "./authContext";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import "dayjs/locale/en";
import ModalPortal from "./ModalPortal";
import { minimumPatientAge, ServerURI } from "../globals";
import { useActiveJobsContext } from "../contexts/ActiveJobsContext";
import { getAuthHeaders } from "./apiUtils";import { X, Trash2, Check, Save, ChevronDown, ChevronRight, Plus, Minus } from 'lucide-react';

// localStorage keys for accordion state
const ACCORDION_CONTACT_KEY = 'patientModal_contactExpanded';
const ACCORDION_TREATMENT_KEY = 'patientModal_treatmentExpanded';

// Clinician type options
const AUTHORIZED_REP_RELATIONSHIPS = [
  'None',
  'Parent',
  'Guardian',
  'Power of Attorney'
];

const CLINICIAN_TYPES = [
  'Primary Care Physician',
  'Psychiatrist',
  'Psychologist',
  'Therapist',
  'Nurse Practitioner',
  'Social Worker',
  'Other'
];

// Empty clinician template
const createEmptyClinician = () => ({
  name: '',
  phone: '',
  email: '',
  type: ''
});

const AddEditPatient = ({ setPatients, isEditPatientOpen, setEditPatientOpen, patientId, onPatientUpdate, onPatientArchived }) => {
  const [newPatientFirstName, setNewPatientFirstName] = useState("");
  const [newPatientMiddleName, setNewPatientMiddleName] = useState("");
  const [newPatientLastName, setNewPatientLastName] = useState("");
  const [newPatientDOB, setNewPatientDOB] = useState(null);
  const [newPatientGender, setNewPatientGender] = useState("");
  const [newPatientPronoun, setNewPatientPronoun] = useState("");
  // Contact information
  const [newPatientPhone, setNewPatientPhone] = useState("");
  const [newPatientEmail, setNewPatientEmail] = useState("");
  const [newPatientAddress, setNewPatientAddress] = useState("");
  // Authorized representative
  const [authRepRelationship, setAuthRepRelationship] = useState("");
  const [authRepName, setAuthRepName] = useState("");
  const [authRepPhone, setAuthRepPhone] = useState("");
  const [authRepEmail, setAuthRepEmail] = useState("");
  // Treatment team - array of clinicians
  const [treatmentTeam, setTreatmentTeam] = useState([createEmptyClinician()]);
  const [isDeleteWarning, setIsDeleteWarning] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const [dobErrorMessage, setDobErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Accordion state - read from localStorage on init
  const [contactExpanded, setContactExpanded] = useState(() => {
    const stored = localStorage.getItem(ACCORDION_CONTACT_KEY);
    return stored === null ? true : stored === 'true';
  });
  const [treatmentExpanded, setTreatmentExpanded] = useState(() => {
    const stored = localStorage.getItem(ACCORDION_TREATMENT_KEY);
    return stored === null ? true : stored === 'true';
  });

  const { currentUser, extendSessionOnAction } = useAuth();
  const userId = currentUser?.uid;
  const { activeJobs = [], pendingJobs = [] } = useActiveJobsContext();
  const hasActiveJobs = patientId && (
    activeJobs.some((j) => j.patientId === patientId) ||
    pendingJobs.some((j) => j.patientId === patientId)
  );

  // Calculate age with one decimal point
  const calculateAge = (dob) => {
    if (!dob || !dob.isValid()) return "";
    const today = dayjs();
    const years = today.diff(dob, 'year', true);
    return `(${years.toFixed(1)} y.o.)`;
  };

  // Reset form to initial/empty state
  const resetForm = () => {
    setNewPatientFirstName("");
    setNewPatientMiddleName("");
    setNewPatientLastName("");
    setNewPatientDOB(null);
    setNewPatientGender("");
    setNewPatientPronoun("");
    setNewPatientPhone("");
    setNewPatientEmail("");
    setNewPatientAddress("");
    setAuthRepRelationship("");
    setAuthRepName("");
    setAuthRepPhone("");
    setAuthRepEmail("");
    setTreatmentTeam([createEmptyClinician()]);
    setDobErrorMessage("");
    setIsDeleteWarning(false);
  };

  useEffect(() => {
    const fetchPatientData = async () => {
      if (patientId) {
        try {
          const patientData = await getData(`/patients/${patientId}`);
          if (patientData) {
            setNewPatientFirstName(patientData.firstName || "");
            setNewPatientMiddleName(patientData.middleName || "");
            setNewPatientLastName(patientData.lastName || "");
            setNewPatientDOB(patientData.dateOfBirth ? dayjs(patientData.dateOfBirth, "MM/DD/YYYY") : null);
            setNewPatientGender(patientData.gender || "");
            setNewPatientPronoun(patientData.pronoun || "");
            // Contact information
            setNewPatientPhone(patientData.phone || "");
            setNewPatientEmail(patientData.email || "");
            setNewPatientAddress(patientData.address || "");
            // Authorized representative
            setAuthRepRelationship(patientData.authorizedRep?.relationship || "");
            setAuthRepName(patientData.authorizedRep?.name || "");
            setAuthRepPhone(patientData.authorizedRep?.phone || "");
            setAuthRepEmail(patientData.authorizedRep?.email || "");
            // Treatment team - handle both new array format and legacy format
            if (patientData.treatmentTeam && Array.isArray(patientData.treatmentTeam) && patientData.treatmentTeam.length > 0) {
              setTreatmentTeam(patientData.treatmentTeam);
            } else if (patientData.primaryCarePhysician) {
              // Migrate legacy data
              setTreatmentTeam([{
                name: patientData.primaryCarePhysician || '',
                phone: patientData.pcpContact || '',
                email: '',
                type: 'Primary Care Physician'
              }]);
            } else {
              setTreatmentTeam([createEmptyClinician()]);
            }
          }
        } catch (error) {
          console.error("Error fetching patient data:", error);
        }
      } else {
        // No patientId means adding new patient - reset form
        resetForm();
      }
    };

    // Reset submitting state when modal opens
    setIsSubmitting(false);
    
    // Only fetch when modal is open - this ensures fresh data on each open
    if (isEditPatientOpen) {
      fetchPatientData();
    }
  }, [patientId, isEditPatientOpen]);

  // Effect to suggest pronouns based on gender if pronouns are not already set
  useEffect(() => {
    if (!newPatientPronoun) {
      switch (newPatientGender) {
        case "Male":
          setNewPatientPronoun("he/him");
          break;
        case "Female":
          setNewPatientPronoun("she/her");
          break;
        case "Non-binary":
          setNewPatientPronoun("they/them");
          break;
        default:
          setNewPatientPronoun("");
      }
    }
  }, [newPatientGender, newPatientPronoun]);

  const handleAddPatient = async () => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    if (!newPatientFirstName.trim()) {
      console.error("First name cannot be empty");
      return;
    }

    if (!newPatientDOB || !newPatientDOB.isValid()) {
      console.error("Invalid Date of Birth");
      return;
    }

    const today = dayjs();
    const maxAllowedDate = today.subtract(minimumPatientAge, 'year');

    if (newPatientDOB.isAfter(today)) {
      console.error("Date of birth cannot be in the future");
      return;
    }

    if (newPatientDOB.isAfter(maxAllowedDate)) {
      console.error(`Patient must be at least ${minimumPatientAge} years old`);
      return;
    }

    if (newPatientDOB.year() < 1900) {
      console.error("Year must be 1900 or later");
      return;
    }

    const patientData = {
      firstName: newPatientFirstName,
      middleName: newPatientMiddleName,
      lastName: newPatientLastName,
      dateOfBirth: newPatientDOB.format("MM/DD/YYYY"),
      age: parseFloat(dayjs().diff(newPatientDOB, 'year', true).toFixed(1)), // Store age as number
      gender: newPatientGender,
      pronoun: newPatientPronoun,
      // Contact information
      phone: newPatientPhone,
      email: newPatientEmail,
      address: newPatientAddress,
      // Authorized representative - only store if any field is filled
      authorizedRep: (authRepRelationship || authRepName || authRepPhone || authRepEmail) ? {
        relationship: authRepRelationship,
        name: authRepName,
        phone: authRepPhone,
        email: authRepEmail,
      } : null,
      // Treatment team - store as array, filter out empty entries
      treatmentTeam: treatmentTeam.filter(c => c.name || c.phone || c.email || c.type),
      modifiedAt: new Date(),
    };

    setIsSubmitting(true);

    try {
      if (patientId) {
        // Update existing patient
        const headers = await getAuthHeaders();
        const resp = await fetch(`${ServerURI}/api/v3/patients/${patientId}`, {
          method: "PUT",
          headers,
          body: JSON.stringify(patientData),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.error || `Update failed (${resp.status})`);
        }

        // Extend session on meaningful user action
        extendSessionOnAction?.();

        patchPatientDirectory(userId, patientId, patientData);

        if (setPatients) {
          setPatients((prevPatients) => prevPatients.map((patient) => (patient.id === patientId ? { ...patient, ...patientData } : patient)));
        }
        if (onPatientUpdate) {
          onPatientUpdate(patientData);
        }

        // Dispatch custom event to notify all components of patient update
        window.dispatchEvent(new CustomEvent('patientUpdated', {
          detail: {
            patientId,
            patientData: { id: patientId, ...patientData }
          }
        }));
      } else {
        // Create new patient
        let newPatientId;

        const headers = await getAuthHeaders();
        const resp = await fetch(`${ServerURI}/api/v3/patients`, {
          method: "POST",
          headers,
          body: JSON.stringify(patientData),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.error || `Create failed (${resp.status})`);
        }
        const result = await resp.json();
        newPatientId = result.patientId;

        // Extend session on meaningful user action
        extendSessionOnAction?.();

        const newPatientWithId = { id: newPatientId, ...patientData, createdAt: new Date(), pin: true };
        patchPatientDirectory(userId, newPatientId, newPatientWithId);

        if (setPatients) {
          setPatients((prevPatients) => [...prevPatients, newPatientWithId]);
        }

        // Dispatch custom event to notify all components of new patient
        window.dispatchEvent(new CustomEvent('patientCreated', {
          detail: {
            patientId: newPatientId,
            patientData: newPatientWithId
          }
        }));

        setNewPatientFirstName("");
        setNewPatientMiddleName("");
        setNewPatientLastName("");
        setNewPatientDOB(null);
        setNewPatientGender("");
        setNewPatientPronoun("");
        // Reset contact information
        setNewPatientPhone("");
        setNewPatientAddress("");
        // Reset treatment team
        setTreatmentTeam([createEmptyClinician()]);
      }

      setEditPatientOpen(false);
    } catch (error) {
      console.error("Error saving patient:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchivePatient = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${ServerURI}/api/v3/patients/${patientId}/archive`, {
        method: "PATCH",
        headers,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `Archive failed (${resp.status})`);
      }

      patchPatientDirectory(userId, patientId, { isArchived: true });

      if (setPatients) {
        setPatients((prevPatients) => prevPatients.map((patient) => (patient.id === patientId ? { ...patient, isArchived: true } : patient)));
      }
      if (onPatientArchived) {
        onPatientArchived();
      }

      setEditPatientOpen(false);
      setIsDeleteWarning(false);
    } catch (error) {
      console.error("Error archiving patient:", error);
      setArchiveError(error.message || "Failed to archive patient.");
      setIsDeleteWarning(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const today = dayjs();
    const maxAllowedDate = today.subtract(minimumPatientAge, 'year');
    
    return (
      newPatientFirstName.trim() && 
      newPatientLastName.trim() && 
      newPatientDOB && 
      newPatientDOB.isValid() && 
      !newPatientDOB.isAfter(today) &&
      !newPatientDOB.isAfter(maxAllowedDate) &&
      newPatientDOB.year() >= 1900
      // Gender and pronouns are optional
    );
  };

  // Add validation function for name fields
  const validateNameInput = (value) => {
    // Allow only letters, spaces, hyphens, and apostrophes
    return value.replace(/[^a-zA-Z\s\-']/g, '');
  };

  // Phone number validation and formatting
  const validatePhoneInput = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX for US numbers
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return digits;
  };

  const validateDateOfBirth = (date) => {
    if (!date || !date.isValid()) {
      setDobErrorMessage("");
      return;
    }

    const today = dayjs();
    const maxAllowedDate = today.subtract(minimumPatientAge, 'year');
    const maxAllowedYear = maxAllowedDate.year();

    if (date.isAfter(today)) {
      setDobErrorMessage("Date of birth cannot be in the future");
    } else if (date.isAfter(maxAllowedDate)) {
      setDobErrorMessage(`Please enter a year between 1900 and ${maxAllowedYear}`);
    } else if (date.year() < 1900) {
      setDobErrorMessage(`Please enter a year between 1900 and ${maxAllowedYear}`);
    } else {
      setDobErrorMessage("");
    }
  };

  return (
    <ModalPortal isOpen={isEditPatientOpen}>
      <div
        className="modal"
        onClick={(event) => {
          // Patient forms may contain substantial unsaved clinical/contact data.
          // Require an explicit Cancel or successful save instead of allowing an
          // accidental backdrop/trackpad click to discard the form silently.
          if (event.target === event.currentTarget) {
            event.preventDefault();
          }
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">{isDeleteWarning ? "Are you sure you want to delete this patient? There's no undo!" : patientId ? "Edit Patient" : "Add New Patient"}</h3>
            {archiveError && <p style={{ color: "var(--error-color, #d32f2f)", margin: "8px 0", fontSize: "0.9em" }}>{archiveError}</p>}
            {!isDeleteWarning && <hr className="modal-header-rule" />}
            {!isDeleteWarning && (
              <div className="modal-form">
                {/* Basic Patient Information Section */}
                <div className="modal-section">
                  <div className="modal-input-row">
                    <TextField 
                      label="First Name" 
                      name="firstName"
                      value={newPatientFirstName} 
                      onChange={(e) => setNewPatientFirstName(validateNameInput(e.target.value))} 
                      className="modal-input" 
                      autoComplete="off" 
                      fullWidth 
                      variant="outlined" 
                    />
                    <TextField 
                      label="Middle Name" 
                      name="middleName"
                      value={newPatientMiddleName} 
                      onChange={(e) => setNewPatientMiddleName(validateNameInput(e.target.value))} 
                      className="modal-input" 
                      autoComplete="off" 
                      fullWidth 
                      variant="outlined" 
                    />
                  </div>
                  <TextField 
                    label="Last Name" 
                    name="lastName"
                    value={newPatientLastName} 
                    onChange={(e) => setNewPatientLastName(validateNameInput(e.target.value))} 
                    className="modal-input userback-ignore" 
                    autoComplete="off" 
                    fullWidth 
                    variant="outlined" 
                  />
                  <div className="modal-input-row">
                    <DatePicker
                      label="Date of Birth"
                      value={newPatientDOB}
                      onChange={(newValue) => {
                        setNewPatientDOB(newValue);
                        validateDateOfBirth(newValue);
                      }}
                      format="MM/DD/YYYY"
                      slotProps={{
                        textField: {
                          className: "modal-input modal-datepicker-field userback-ignore",
                          size: "small",
                          inputProps: {
                            autoComplete: "off",
                          },
                        },
                        popper: {
                          className: "modal-datepicker-popper",
                        },
                      }}
                    />
                    <TextField 
                      label="Age" 
                      name="age"
                      value={calculateAge(newPatientDOB)} 
                      className="modal-input" 
                      fullWidth 
                      variant="outlined" 
                      disabled
                      placeholder="(0.0 y.o.)"
                    />
                  </div>
                  {dobErrorMessage && (
                    <div className="error-message">
                      {dobErrorMessage}
                    </div>
                  )}
                  <div className="modal-radio">
                    <label>
                      <input type="radio" name="gender" value="Male" checked={newPatientGender === "Male"} onChange={(e) => setNewPatientGender(e.target.value)} />
                      Male
                    </label>
                    <label>
                      <input type="radio" name="gender" value="Female" checked={newPatientGender === "Female"} onChange={(e) => setNewPatientGender(e.target.value)} />
                      Female
                    </label>
                    <label>
                      <input type="radio" name="gender" value="Non-binary" checked={newPatientGender === "Non-binary"} onChange={(e) => setNewPatientGender(e.target.value)} />
                      Non-binary
                    </label>
                    <label>
                      <input type="radio" name="gender" value="Prefer not to say" checked={newPatientGender === "Prefer not to say"} onChange={(e) => setNewPatientGender(e.target.value)} />
                      Prefer not to say
                    </label>
                  </div>
                  <div className="modal-dropdown">
                    <label htmlFor="pronoun-select" className="modal-section-label">
                      Pronouns:
                    </label>
                    <select id="pronoun-select" name="pronoun" value={newPatientPronoun} onChange={(e) => setNewPatientPronoun(e.target.value)} className="modal-select">
                      <option value="">Select Pronouns</option>
                      <option value="he/him">He/Him</option>
                      <option value="she/her">She/Her</option>
                      <option value="they/them">They/Them</option>
                    </select>
                  </div>
                </div>

                {/* Contact Information Section - Collapsible */}
                <div className="modal-section">
                  <button
                    type="button"
                    className="modal-accordion-header"
                    onClick={() => {
                      const newValue = !contactExpanded;
                      setContactExpanded(newValue);
                      localStorage.setItem(ACCORDION_CONTACT_KEY, String(newValue));
                    }}
                  >
                    {contactExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <span>Contact Information</span>
                  </button>
                  {contactExpanded && (
                    <div className="modal-accordion-content">
                  <div className="modal-input-row">
                    <TextField 
                      label="Phone Number" 
                      name="phone"
                      value={newPatientPhone} 
                      onChange={(e) => setNewPatientPhone(validatePhoneInput(e.target.value))} 
                      className="modal-input" 
                      autoComplete="off" 
                      fullWidth 
                      variant="outlined" 
                      placeholder="(555) 123-4567"
                      slotProps={{
                        htmlInput: { maxLength: 14 }
                      }}
                    />
                    <TextField 
                      label="Email Address" 
                      name="email"
                      value={newPatientEmail} 
                      onChange={(e) => setNewPatientEmail(e.target.value)} 
                      className="modal-input" 
                      autoComplete="off" 
                      fullWidth 
                      variant="outlined" 
                      placeholder="patient@email.com"
                    />
                  </div>
                  <TextField 
                    label="Home Address" 
                    name="address"
                    value={newPatientAddress} 
                    onChange={(e) => setNewPatientAddress(e.target.value)} 
                    className="modal-input" 
                    autoComplete="off" 
                    fullWidth 
                    variant="outlined" 
                    placeholder="123 Main St, City, State 12345"
                  />

                  {/* Authorized Representative */}
                  <div className="modal-section-label" style={{ marginTop: '12px', marginBottom: '4px' }}>Authorized Representative</div>
                  <div className="modal-input-row">
                    <TextField
                      label="Name"
                      name="authRepName"
                      value={authRepName}
                      onChange={(e) => setAuthRepName(e.target.value)}
                      className="modal-input"
                      autoComplete="off"
                      fullWidth
                      variant="outlined"
                    />
                    <select
                      name="authRepRelationship"
                      value={authRepRelationship}
                      onChange={(e) => setAuthRepRelationship(e.target.value)}
                      className="modal-select modal-clinician-type-select"
                    >
                      <option value="">Relationship</option>
                      {AUTHORIZED_REP_RELATIONSHIPS.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-input-row">
                    <TextField
                      label="Phone Number"
                      name="authRepPhone"
                      value={authRepPhone}
                      onChange={(e) => setAuthRepPhone(validatePhoneInput(e.target.value))}
                      className="modal-input"
                      autoComplete="off"
                      fullWidth
                      variant="outlined"
                      placeholder="(555) 123-4567"
                      slotProps={{
                        htmlInput: { maxLength: 14 }
                      }}
                    />
                    <TextField
                      label="Email Address"
                      name="authRepEmail"
                      value={authRepEmail}
                      onChange={(e) => setAuthRepEmail(e.target.value)}
                      className="modal-input"
                      autoComplete="off"
                      fullWidth
                      variant="outlined"
                      placeholder="representative@email.com"
                    />
                  </div>
                    </div>
                  )}
                </div>

                {/* Treatment Team Section - Collapsible */}
                <div className="modal-section">
                  <button
                    type="button"
                    className="modal-accordion-header"
                    onClick={() => {
                      const newValue = !treatmentExpanded;
                      setTreatmentExpanded(newValue);
                      localStorage.setItem(ACCORDION_TREATMENT_KEY, String(newValue));
                    }}
                  >
                    {treatmentExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <span>Treatment Team</span>
                  </button>
                  {treatmentExpanded && (
                    <div className="modal-accordion-content">
                      {treatmentTeam.map((clinician, index) => (
                        <div key={index} className="modal-treatment-team-row">
                          <div className="modal-input-row">
                            <TextField 
                              label="Physician Name" 
                              value={clinician.name} 
                              onChange={(e) => {
                                const updated = [...treatmentTeam];
                                updated[index] = { ...updated[index], name: e.target.value };
                                setTreatmentTeam(updated);
                              }} 
                              className="modal-input" 
                              autoComplete="off" 
                              fullWidth 
                              variant="outlined" 
                              placeholder="Dr. Smith"
                            />
                            <select
                              value={clinician.type}
                              onChange={(e) => {
                                const updated = [...treatmentTeam];
                                updated[index] = { ...updated[index], type: e.target.value };
                                setTreatmentTeam(updated);
                              }}
                              className="modal-select modal-clinician-type-select"
                            >
                              <option value="">Select Type</option>
                              {CLINICIAN_TYPES.map(type => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                          </div>
                          <div className="modal-input-row">
                            <TextField 
                              label="Phone" 
                              value={clinician.phone} 
                              onChange={(e) => {
                                const updated = [...treatmentTeam];
                                updated[index] = { ...updated[index], phone: validatePhoneInput(e.target.value) };
                                setTreatmentTeam(updated);
                              }} 
                              className="modal-input" 
                              autoComplete="off" 
                              fullWidth 
                              variant="outlined" 
                              placeholder="(555) 123-4567"
                              slotProps={{
                                htmlInput: { maxLength: 14 }
                              }}
                            />
                            <TextField 
                              label="Email" 
                              value={clinician.email} 
                              onChange={(e) => {
                                const updated = [...treatmentTeam];
                                updated[index] = { ...updated[index], email: e.target.value };
                                setTreatmentTeam(updated);
                              }} 
                              className="modal-input" 
                              autoComplete="off" 
                              fullWidth 
                              variant="outlined" 
                              placeholder="doctor@clinic.com"
                            />
                            {treatmentTeam.length > 1 && (
                              <button
                                type="button"
                                className="modal-treatment-team-remove-btn"
                                onClick={() => {
                                  setTreatmentTeam(treatmentTeam.filter((_, i) => i !== index));
                                }}
                                title="Remove clinician"
                              >
                                <Minus size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {/* Only show Add button if the last clinician has at least one field filled */}
                      {(treatmentTeam.length === 0 || 
                        treatmentTeam[treatmentTeam.length - 1].name || 
                        treatmentTeam[treatmentTeam.length - 1].phone || 
                        treatmentTeam[treatmentTeam.length - 1].email || 
                        treatmentTeam[treatmentTeam.length - 1].type) && (
                        <button
                          type="button"
                          className="modal-treatment-team-add-btn"
                          onClick={() => setTreatmentTeam([...treatmentTeam, createEmptyClinician()])}
                        >
                          <Plus size={14} /> Add Clinician
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="modal-button-container">
              <button
                className="button-cancel-secondary"
                onClick={() => {
                  resetForm();
                  setEditPatientOpen(false);
                }}>
                <X size={15} /> Cancel
              </button>
              {patientId && (
                <button
                  className="button-delete"
                  disabled={isSubmitting || hasActiveJobs}
                  title={hasActiveJobs ? "Patient has recordings being processed" : undefined}
                  onClick={() => {
                    setArchiveError("");
                    if (isDeleteWarning) {
                      handleArchivePatient();
                    } else {
                      setIsDeleteWarning(true);
                    }
                  }}>
                  <Trash2 size={15} /> {hasActiveJobs ? "Processing..." : isSubmitting && isDeleteWarning ? "Deleting..." : (isDeleteWarning ? "Yes Delete Patient" : "Delete Patient")}
                </button>
              )}
              {!isDeleteWarning && (
                <button className="button-confirm" onClick={handleAddPatient} disabled={!isFormValid() || isSubmitting}>
                  <Save size={15} /> {isSubmitting ? "Saving..." : (patientId ? "Update Patient" : "Add Patient")}
                </button>
              )}
            </div>
          </div>
        </LocalizationProvider>
      </div>
    </ModalPortal>
  );
};

export default AddEditPatient;
